import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  useToast,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

const WHATSAPP_PAYMENT_LINK_SUCCESS_STORAGE_KEY =
  "pickdenguWhatsAppPaymentLinkSuccess";

const cardsBorderRadii ="8px";

type WhatsAppPaymentLinkSuccessPayload = {
  checkoutChannel: "whatsapp";
  orderId: string;
  txRef: string;
  checkoutUrl: string;
  amount: number;
  currency: string;
  businessWhatsAppNumber: string;
  recipientPhone?: string;
  paymentLinkSent: boolean;
  notificationReason?: string;
  createdAt: number;
  expiresAt: number;
};

const normalizeWhatsAppNumber = (value: unknown) =>
  String(value || "").replace(/[^\d]/g, "");

const readWhatsAppPaymentLinkSuccess =
  (): WhatsAppPaymentLinkSuccessPayload | null => {
    if (typeof window === "undefined") {
      return null;
    }

    try {
      const rawValue = window.sessionStorage.getItem(
        WHATSAPP_PAYMENT_LINK_SUCCESS_STORAGE_KEY,
      );

      if (!rawValue) {
        return null;
      }

      const parsed = JSON.parse(rawValue) as WhatsAppPaymentLinkSuccessPayload;

      if (
        parsed.checkoutChannel !== "whatsapp" ||
        !parsed.orderId ||
        !Number.isFinite(Number(parsed.expiresAt)) ||
        Number(parsed.expiresAt) <= Date.now()
      ) {
        window.sessionStorage.removeItem(
          WHATSAPP_PAYMENT_LINK_SUCCESS_STORAGE_KEY,
        );

        return null;
      }

      return parsed;
    } catch (error) {
      console.error("Failed to read WhatsApp payment confirmation:", error);

      return null;
    }
  };

const clearWhatsAppPaymentLinkSuccess = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(WHATSAPP_PAYMENT_LINK_SUCCESS_STORAGE_KEY);
};

const CheckoutSuccessPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { theme } = useCustomTheme();

  const [order, setOrder] = useState<any>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [whatsAppPaymentLink, setWhatsAppPaymentLink] =
    useState<WhatsAppPaymentLinkSuccessPayload | null>(null);
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);

  const isDark = theme.name === "dark";

  const pageBg = theme.background;
  const cardBg = theme.surface;
  const textColor = theme.text;
  const mutedText = theme.textMuted;
  const borderColor = theme.border;
  const borderStrong = theme.borderStrong;

  const primaryButtonStyles = {
    color: theme.textOnPrimary,
    bgImage: theme.gradients?.primaryButton || theme.primary,
    boxShadow: theme.shadowSm,
    _hover: {
      bgImage: theme.gradients?.primaryButtonHover || theme.primaryHover,
      transform: "translateY(-1px)",
      boxShadow: theme.shadowMd,
    },
    _active: {
      transform: "translateY(0)",
    },
    borderRadius: {cardsBorderRadii},
    transition: "all 0.2s ease",
  };

  const secondaryButtonStyles = {
    color: textColor,
    bg: isDark ? "rgba(255,255,255,0.04)" : "white",
    border: `1px solid ${borderColor}`,
    _hover: {
      bg: isDark ? "rgba(255,255,255,0.07)" : "rgba(42,44,59,0.03)",
      borderColor: borderStrong,
    },
    borderRadius: {cardsBorderRadii},
    transition: "all 0.2s ease",
  };

  const getInvoiceOpenUrl = () => {
    return `${process.env.REACT_APP_BACKEND_URL}/api/v1/invoices/open/${orderId}`;
  };

  const getInvoiceDownloadUrl = () => {
    return `${process.env.REACT_APP_BACKEND_URL}/api/v1/invoices/download/${orderId}`;
  };

  const getOrderUrl = () => {
    return `${process.env.REACT_APP_BACKEND_URL}/api/v1/orders/${orderId}`;
  };

  const isWhatsAppOrMobileBrowser = () => {
    const userAgent = window.navigator.userAgent || "";
    const isWhatsApp = /WhatsApp/i.test(userAgent);
    const isMobile =
      /Android|iPhone|iPad|iPod|IEMobile|Opera Mini|Mobile/i.test(userAgent);

    return isWhatsApp || isMobile;
  };

  const shouldOpenInvoiceDirectly = isWhatsAppOrMobileBrowser();

  useEffect(() => {
    if (!orderId) return;

    const fetchOrder = async () => {
      setLoadingOrder(true);

      try {
        const res = await axios.get(getOrderUrl());
        const data = res.data?.order || res.data?.data || res.data;

        setOrder(data || null);
      } catch {
        setOrder(null);
      } finally {
        setLoadingOrder(false);
      }
    };

    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (!orderId) {
      return;
    }

    const storedPaymentLink = readWhatsAppPaymentLinkSuccess();

    /*
     * This is the only condition that enables the WhatsApp modal and
     * button. Normal web checkout never stores a WhatsApp payload and
     * therefore never sees the WhatsApp action.
     */
    if (
      !storedPaymentLink ||
      storedPaymentLink.checkoutChannel !== "whatsapp" ||
      storedPaymentLink.orderId !== orderId
    ) {
      setWhatsAppPaymentLink(null);
      setIsWhatsAppModalOpen(false);
      return;
    }

    setWhatsAppPaymentLink(storedPaymentLink);
    setIsWhatsAppModalOpen(true);
  }, [orderId]);

  const isOrderPaid = useMemo(() => {
    const paymentStatus = String(
      order?.paymentStatus ||
        order?.payment?.status ||
        order?.payment?.paymentStatus ||
        order?.status ||
        "",
    ).toLowerCase();

    return (
      order?.isPaid === true ||
      order?.paid === true ||
      paymentStatus === "paid" ||
      paymentStatus === "successful" ||
      paymentStatus === "success" ||
      paymentStatus === "completed"
    );
  }, [order]);

  const paymentBadgeText = loadingOrder
    ? "Checking Payment Status"
    : isOrderPaid
      ? "Payment Successful"
      : "Pending Payment";

  const paymentBadgeColor = loadingOrder
    ? "blue"
    : isOrderPaid
      ? "green"
      : "yellow";

  const headingText = isOrderPaid
    ? "Order Successful"
    : "Order Pending Payment";

  const descriptionText = loadingOrder
    ? "We are checking your payment status. Your invoice is ready."
    : isOrderPaid
      ? "Thank you for your order. Your payment was successful and your invoice is ready."
      : "Thank you for your order. Your order has been created and is pending payment. You can use the invoice to complete or confirm your payment.";

  /*
   * Use the exact business WhatsApp number configured for the frontend.
   * The stored number is only a fallback for an already-created checkout.
   */
  const businessWhatsAppNumber = normalizeWhatsAppNumber(
    process.env.REACT_APP_WHATSAPP_BUSINESS_NUMBER ||
      process.env.REACT_APP_WHATSAPP_NUMBER ||
      whatsAppPaymentLink?.businessWhatsAppNumber ||
      "",
  );

  const whatsAppUrl = useMemo(() => {
    if (!whatsAppPaymentLink || !businessWhatsAppNumber) {
      return "";
    }

    /*
     * The payment link has already been sent to the customer in
     * WhatsApp. Open the business chat directly without prefilled text.
     */
    return `https://wa.me/${businessWhatsAppNumber}`;
  }, [businessWhatsAppNumber, whatsAppPaymentLink]);

  const goToWhatsApp = () => {
    if (!whatsAppPaymentLink) {
      return;
    }

    if (!whatsAppUrl) {
      toast({
        title: "WhatsApp number is unavailable",
        description:
          "Add REACT_APP_WHATSAPP_BUSINESS_NUMBER to the frontend environment and restart the application.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    clearWhatsAppPaymentLinkSuccess();
    setIsWhatsAppModalOpen(false);
    window.location.assign(whatsAppUrl);
  };

  if (!orderId) {
    return (
      <Box
        minH="100dvh"
        w="full"
        bg={pageBg}
        p={{ base: 3, md: 4 }}
        display="flex"
        flexDirection="column"
      >
        <Box
          w="full"
          maxW="600px"
          mx="auto"
          flex="1"
          p={{ base: 5, md: 6 }}
          bg={cardBg}
          boxShadow={theme.shadowMd}
          borderRadius={cardsBorderRadii}
          border={`1px solid ${borderColor}`}
          backdropFilter={isDark ? "blur(8px)" : undefined}
        >
          <Heading size="md" color={textColor}>
            Invalid order
          </Heading>
        </Box>
      </Box>
    );
  }

  const openInvoice = () => {
    if (!orderId) return;

    window.location.href = getInvoiceOpenUrl();
  };

  const downloadInvoice = async () => {
    if (!orderId) return;

    /**
     * Mobile / WhatsApp:
     * Do not use blob download because WhatsApp often blocks it.
     * Open the invoice PDF directly instead.
     */
    if (shouldOpenInvoiceDirectly) {
      openInvoice();
      return;
    }

    /**
     * Desktop:
     * Keep the original blob download because it works well on
     * Chrome/Edge/Desktop.
     */
    try {
      const res = await axios.get(getInvoiceDownloadUrl(), {
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `invoice-${orderId}.pdf`;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch {
      /**
       * Fallback:
       * If desktop download fails for any reason, open the PDF
       * directly.
       */
      openInvoice();

      toast({
        title: "Opening invoice instead",
        description: "Download was blocked, so we opened the invoice directly.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const resendInvoiceEmail = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/invoices/from-order/${orderId}`,
      );

      toast({
        title: "Invoice email sent",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch {
      toast({
        title: "Failed to send invoice email",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Box
        minH="100dvh"
        w="full"
        mx="auto"
        bg={pageBg}
        p={{ base: 3, md: 4 }}
        display="flex"
        flexDirection="column"
      >
        <Box
          flex="1"
          minH={0}
          w="full"
          mx="auto"
          p={{ base: 5, md: 6 }}
          bg={cardBg}
          borderRadius={cardsBorderRadii}
          border={`1px solid ${borderColor}`}
          boxShadow={theme.shadowMd}
          display="flex"
          flexDirection="column"
        >
          <VStack spacing={4} align="stretch" flex="1">
            <Badge
              colorScheme={paymentBadgeColor}
              alignSelf="flex-start"
              px={3}
              py={1}
              borderRadius={cardsBorderRadii}
            >
              {paymentBadgeText}
            </Badge>

            <Heading size="lg" color={theme.brand?.electricBlue || textColor}>
              {headingText}
            </Heading>

            <Text color={textColor}>{descriptionText}</Text>

            <Text fontSize="sm" color={mutedText} wordBreak="break-word">
              Order ID: {orderId}
            </Text>

            {whatsAppPaymentLink ? (
              <Box
                p={4}
                borderRadius={cardsBorderRadii}
                border="1px solid"
                borderColor={borderColor}
                bg={theme.surfaceAlt}
              >
                <Text color={textColor} fontWeight="800">
                  Complete your payment in WhatsApp
                </Text>

                <Text mt={1} mb={3} color={mutedText} fontSize="sm">
                  The secure payment link for order {orderId} was sent to
                  WhatsApp. Return to the business chat and tap the link.
                </Text>

                <Button
                  w={{
                    base: "full",
                    md: "auto",
                  }}
                  {...primaryButtonStyles}
                  onClick={goToWhatsApp}
                  isDisabled={!whatsAppUrl}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  flexShrink={0}
                >
                  Go to WhatsApp
                </Button>
              </Box>
            ) : null}

            <Button
              w={{
                base: "full",
                md: "auto",
              }}
              {...primaryButtonStyles}
              onClick={downloadInvoice}
              whiteSpace="nowrap"
            >
              {shouldOpenInvoiceDirectly ? "Open Invoice" : "Download Invoice"}
            </Button>

            <Button
              w={{
                base: "full",
                md: "auto",
              }}
              {...secondaryButtonStyles}
              onClick={resendInvoiceEmail}
              whiteSpace="nowrap"
            >
              Resend Invoice Email
            </Button>

            <Button
              w={{
                base: "full",
                md: "auto",
              }}
              variant="ghost"
              color={textColor}
              _hover={{
                bg: isDark ? "rgba(255,255,255,0.05)" : "rgba(42,44,59,0.04)",
              }}
              onClick={() => navigate("/products")}
              whiteSpace="nowrap"
            >
              ← Continue Shopping
            </Button>
          </VStack>
        </Box>
      </Box>

      <Modal
        isOpen={Boolean(whatsAppPaymentLink) && isWhatsAppModalOpen}
        onClose={() => setIsWhatsAppModalOpen(false)}
        isCentered
        size="sm"
        closeOnOverlayClick={false}
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />

        <ModalContent
          mx={4}
          bg={cardBg}
          color={textColor}
          border="1px solid"
          borderColor={borderColor}
          borderRadius={cardsBorderRadii}
          boxShadow={theme.shadowMd}
        >
          <ModalHeader pb={2}>Payment link created</ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <Text color={textColor} fontWeight="800">
              Payment link created for order {orderId}
            </Text>

            <Text mt={2} color={mutedText} fontSize="sm">
              The secure PayChangu payment link has been sent to WhatsApp. Go
              back to the business chat and tap the link to complete your
              payment.
            </Text>

            <Box
              mt={4}
              p={3}
              borderRadius={cardsBorderRadii}
              bg={theme.surfaceAlt}
              border="1px solid"
              borderColor={borderColor}
            >
              <Text color={textColor} fontWeight="800">
                Complete payment in WhatsApp
              </Text>

              <Text mt={1} color={mutedText} fontSize="sm">
                Order {orderId}
              </Text>
            </Box>

            {!businessWhatsAppNumber ? (
              <Text mt={3} color="red.400" fontSize="sm">
                The WhatsApp business number is not configured.
              </Text>
            ) : null}
          </ModalBody>

          <ModalFooter
            gap={2}
            flexDirection={{
              base: "column",
              sm: "row",
            }}
            flexWrap="nowrap"
          >
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsWhatsAppModalOpen(false)}
              w={{
                base: "100%",
                sm: "auto",
              }}
              color={mutedText}
              whiteSpace="nowrap"
              flexShrink={0}
            >
              Stay Here
            </Button>

            <Button
              type="button"
              onClick={goToWhatsApp}
              isDisabled={!whatsAppUrl}
              w={{
                base: "100%",
                sm: "auto",
              }}
              {...primaryButtonStyles}
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              flexShrink={0}
            >
              Go to WhatsApp
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CheckoutSuccessPage;
